import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import RCSMessageEditor from '../components/RCSMessageEditor';
import RCSMobilePreview from '../components/RCSMobilePreview';
import TemplateSelector from '../components/TemplateSelector';
import ContactFileSelector from '../components/ContactFileSelector';
import SMSPreviewModal from '../components/SMSPreviewModal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Send, Users, CheckCircle, XCircle, Smartphone, 
  MessageSquare, AlertTriangle, Loader2, Upload, LayoutGrid, X,
  FileSpreadsheet, Phone, Eye, MessageCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SendRCS({ embedded = false }) {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const editCampaignId = searchParams.get('edit');
  
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [recipients, setRecipients] = useState('');
  const [messageContent, setMessageContent] = useState(null);
  const [capabilityResults, setCapabilityResults] = useState(null);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [sendResults, setSendResults] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [appliedTemplate, setAppliedTemplate] = useState(null);
  const [editorKey, setEditorKey] = useState(0); // Force re-render of editor
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignName, setCampaignName] = useState('');
  
  // Contact file state
  const [contactFileSelectorOpen, setContactFileSelectorOpen] = useState(false);
  const [selectedContactFile, setSelectedContactFile] = useState(null);
  const [recipientSource, setRecipientSource] = useState('manual'); // 'manual' | 'file'
  
  // SMS Preview state
  const [smsPreviewOpen, setSmsPreviewOpen] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [isMarketing, setIsMarketing] = useState(true);
  const [smsFallbackEnabled, setSmsFallbackEnabled] = useState(true);

  // Memoized callback to prevent infinite loops
  const handlePreviewChange = useCallback((content) => {
    setMessageContent(content);
  }, []);

  // Fetch clients for SuperAdmin
  useEffect(() => {
    if (user?.role === 'superadmin') {
      fetchClients();
    }
    fetchTemplates();
  }, [user, token]);

  // Load campaign in edit mode
  useEffect(() => {
    if (editCampaignId && token) {
      loadCampaignForEdit(editCampaignId);
    }
  }, [editCampaignId, token]);

  const loadCampaignForEdit = async (campaignId) => {
    try {
      const response = await fetch(`${API_URL}/api/v2/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const campaign = await response.json();
        setIsEditMode(true);
        setEditingCampaign(campaign);
        setCampaignName(campaign.name || '');
        
        // Set client if superadmin
        if (campaign.client_id) {
          setSelectedClient(campaign.client_id);
        }
        
        // Set recipients
        if (campaign.recipients && campaign.recipients.length > 0) {
          setRecipients(campaign.recipients.join('\n'));
        }
        
        // Set message content for the editor
        if (campaign.message_content) {
          setMessageContent(campaign.message_content);
          setEditorKey(prev => prev + 1); // Force editor re-render
        }
        
        // Set message type
        if (campaign.message_type) {
          setIsMarketing(campaign.message_type === 'marketing');
        }
        
        // Set SMS fallback
        if (typeof campaign.sms_fallback_enabled === 'boolean') {
          setSmsFallbackEnabled(campaign.sms_fallback_enabled);
        }
        
        toast.success(`Campagne "${campaign.name}" chargée pour édition`);
      } else {
        toast.error('Impossible de charger la campagne');
      }
    } catch (err) {
      console.error('Error loading campaign:', err);
      toast.error('Erreur lors du chargement de la campagne');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v2/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const getPhonesList = () => {
    return recipients
      .split(/[\n,;]+/)
      .map(p => p.trim())
      .filter(p => p.length > 0);
  };

  const handleCheckCapabilities = async () => {
    const phones = getPhonesList();
    if (phones.length === 0) {
      toast.error('Veuillez entrer au moins un numéro de téléphone');
      return;
    }

    if (user?.role === 'superadmin' && !selectedClient) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    setChecking(true);
    setCapabilityResults(null);

    try {
      const clientParam = user?.role === 'superadmin' ? `?client_id=${selectedClient}` : '';
      const response = await fetch(`${API_URL}/api/v2/rcs/campaign-preview${clientParam}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ phones })
      });

      if (response.ok) {
        const data = await response.json();
        setCapabilityResults(data);
        toast.success('Vérification terminée');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la vérification');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setChecking(false);
    }
  };

  const handleSendMessages = async () => {
    if (!messageContent) {
      toast.error('Veuillez créer un message');
      return;
    }

    const phones = getPhonesList();
    if (phones.length === 0) {
      toast.error('Veuillez entrer des destinataires');
      return;
    }

    if (user?.role === 'superadmin' && !selectedClient) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    setSending(true);
    setSendResults(null);

    try {
      const clientParam = user?.role === 'superadmin' ? `?client_id=${selectedClient}` : '';
      const response = await fetch(`${API_URL}/api/v2/rcs/send-bulk${clientParam}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          recipients: phones,
          message_type: messageContent.message_type,
          content: messageContent.content,
          traffic_type: messageContent.traffic_type,
          auto_sms_fallback: true
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSendResults(data);
        toast.success(`Messages envoyés: ${data.rcs_sent} RCS, ${data.sms_fallback} SMS`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setSending(false);
    }
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    if (templateId === 'new') {
      setMessageContent(null);
      setAppliedTemplate(null);
      return;
    }
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setMessageContent({
        message_type: template.message_type,
        traffic_type: 'PROMOTION',
        content: template.content
      });
    }
  };

  // Handle template selection from new modal
  const handleTemplateFromSelector = (templateData) => {
    setAppliedTemplate(templateData.template);
    setMessageContent({
      message_type: templateData.message_type,
      traffic_type: templateData.traffic_type,
      content: templateData.content
    });
    // Increment key to force editor re-render with new content
    setEditorKey(prev => prev + 1);
  };

  // Clear applied template
  const handleClearTemplate = () => {
    setAppliedTemplate(null);
    setMessageContent(null);
    setEditorKey(prev => prev + 1);
  };

  // Handle contact file selection
  const handleContactFileSelect = (fileData) => {
    setSelectedContactFile(fileData);
    setRecipientSource('file');
    // Pre-fill recipients with phone numbers for display
    const phones = fileData.contacts.map(c => c.mobile).join('\n');
    setRecipients(phones);
  };

  // Clear contact file
  const handleClearContactFile = () => {
    setSelectedContactFile(null);
    setRecipientSource('manual');
    setRecipients('');
  };

  // Get template variables (for mapping)
  const getTemplateVariables = () => {
    if (!appliedTemplate?.variables) return [];
    return appliedTemplate.variables;
  };

  const content = (
    <>
      {/* Page Title */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
          {isEditMode ? `Éditer la campagne : ${campaignName}` : 'Nouvelle Campagne RCS'}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          {isEditMode 
            ? 'Modifiez les paramètres de votre campagne et sauvegardez'
            : 'Créez et envoyez des messages RCS enrichis à vos destinataires'
          }
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="send-rcs-page">
        {/* Left Column - Editor */}
        <div className="space-y-6">
          {/* Client Selection (SuperAdmin only) - OBLIGATOIRE */}
          {user?.role === 'superadmin' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  Sélection du client
                  {!selectedClient && (
                    <Badge variant="destructive" className="text-xs">Obligatoire</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Sélectionnez le client pour créer la campagne
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={selectedClient} onValueChange={(value) => setSelectedClient(value === '__none__' ? '' : value)}>
                  <SelectTrigger data-testid="client-selector">
                    <SelectValue placeholder="Choisir un client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" className="text-slate-500 italic">
                      -- Aucun client sélectionné --
                    </SelectItem>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} - {client.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          )}

          {/* Message d'information si pas de client sélectionné (SUPADMIN) */}
          {user?.role === 'superadmin' && !selectedClient && (
            <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-200 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-indigo-800">Sélectionnez un client pour activer les options</p>
                <p className="text-sm text-indigo-600 mt-1">Les modules ci-dessous sont en lecture seule tant qu'aucun client n'est sélectionné.</p>
              </div>
            </div>
          )}

          {/* Wrapper pour griser les modules si pas de client (SUPADMIN) */}
          <div className={`space-y-6 ${user?.role === 'superadmin' && !selectedClient ? 'opacity-50 pointer-events-none' : ''}`}>
              {/* Recipients */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Destinataires
                  </CardTitle>
                  <CardDescription>
                    Importez un fichier de contacts ou entrez les numéros manuellement
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Source selector */}
                  <div className="flex gap-2">
                    <Button
                      variant={recipientSource === 'file' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setContactFileSelectorOpen(true)}
                      className={recipientSource === 'file' ? 'bg-indigo-600' : ''}
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Fichier importé
                    </Button>
                    <Button
                      variant={recipientSource === 'manual' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setRecipientSource('manual');
                        setSelectedContactFile(null);
                      }}
                      className={recipientSource === 'manual' ? 'bg-indigo-600' : ''}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Saisie manuelle
                    </Button>
                  </div>

                  {/* Contact file display with VARIABLES PREVIEW */}
                  {selectedContactFile && recipientSource === 'file' && (
                    <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                            <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-indigo-900">{selectedContactFile.file_name}</p>
                            <p className="text-xs text-indigo-600">
                              {selectedContactFile.total_valid} contacts valides
                              {selectedContactFile.rcs_count !== undefined && (
                                <> • {selectedContactFile.rcs_count} RCS / {selectedContactFile.sms_count} SMS</>
                              )}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={handleClearContactFile}
                          className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Retirer
                        </Button>
                      </div>
                      
                      {/* Variables disponibles dans le fichier */}
                      {selectedContactFile.variable_columns?.length > 0 && (
                        <div className="pt-3 border-t border-indigo-200">
                          <p className="text-xs font-medium text-indigo-700 mb-2">
                            Variables disponibles ({selectedContactFile.variable_columns.length}) :
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {selectedContactFile.variable_columns.map((col, idx) => (
                              <Badge 
                                key={idx} 
                                variant="secondary" 
                                className="bg-white text-indigo-700 border border-indigo-200 text-xs font-mono"
                              >
                                {`{${col}}`}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-[10px] text-indigo-500 mt-2">
                            Utilisez ces variables dans votre message pour personnaliser chaque envoi
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Variable mapping info */}
                  {selectedContactFile && appliedTemplate?.variables?.length > 0 && (
                    <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <p className="text-sm font-medium text-indigo-800 mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Variables du template
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {appliedTemplate.variables.map(v => {
                          const mapped = selectedContactFile.variable_mapping?.[v];
                          return (
                            <Badge 
                              key={v} 
                              variant={mapped ? 'default' : 'outline'}
                              className={mapped ? 'bg-green-600' : 'border-indigo-400 text-indigo-700'}
                            >
                              {`{${v}}`} {mapped ? `→ ${mapped}` : '(non mappé)'}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Manual input (shown only if manual mode) */}
                  {recipientSource === 'manual' && (
                    <Textarea
                      value={recipients}
                      onChange={(e) => setRecipients(e.target.value)}
                      placeholder="+33612345678&#10;+33698765432&#10;+33611223344"
                      rows={4}
                      data-testid="recipients-input"
                    />
                  )}
              
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {recipientSource === 'file' && selectedContactFile 
                        ? `${selectedContactFile.total_valid} contact(s) du fichier`
                        : `${getPhonesList().length} numéro(s) détecté(s)`
                      }
                    </span>
                
                    <Button
                      onClick={handleCheckCapabilities}
                      disabled={checking || getPhonesList().length === 0}
                      variant="outline"
                      data-testid="check-capabilities-btn"
                    >
                      {checking ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Vérification...
                        </>
                      ) : (
                        <>
                          <Smartphone className="w-4 h-4 mr-2" />
                          Vérifier RCS
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Capability Results */}
                  {capabilityResults && (
                    <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-green-600">
                            {capabilityResults.summary?.rcs || 0}
                          </div>
                          <div className="text-xs text-gray-500">RCS</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-indigo-600">
                            {capabilityResults.summary?.sms || 0}
                          </div>
                          <div className="text-xs text-gray-500">SMS Fallback</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-indigo-600">
                            {capabilityResults.summary?.rcs_percentage || 0}%
                          </div>
                          <div className="text-xs text-gray-500">Taux RCS</div>
                        </div>
                      </div>
                      
                      {capabilityResults.estimated_cost && (
                        <div className="pt-3 border-t text-sm">
                          <div className="flex justify-between">
                            <span>Coût estimé RCS:</span>
                            <span className="font-medium">{capabilityResults.estimated_cost.rcs_cost}€</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Coût estimé SMS:</span>
                            <span className="font-medium">{capabilityResults.estimated_cost.sms_cost}€</span>
                          </div>
                          <div className="flex justify-between font-bold pt-2 border-t mt-2">
                            <span>Total:</span>
                            <span>{capabilityResults.estimated_cost.total_cost}€</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

          {/* Template Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <LayoutGrid className="w-5 h-5" />
                Template
              </CardTitle>
              <CardDescription>
                Utilisez un template prédéfini ou créez votre message
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {appliedTemplate ? (
                <div className="flex items-center justify-between p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <LayoutGrid className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-medium text-indigo-900">{appliedTemplate.name}</p>
                      <p className="text-xs text-indigo-600">{appliedTemplate.industry} • {appliedTemplate.category}</p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleClearTemplate}
                    className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Retirer
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  className="w-full h-20 border-dashed border-2 hover:border-indigo-300 hover:bg-indigo-50/50"
                  onClick={() => setTemplateSelectorOpen(true)}
                  data-testid="open-template-selector"
                >
                  <div className="flex flex-col items-center gap-1">
                    <LayoutGrid className="w-6 h-6 text-indigo-600" />
                    <span className="text-sm font-medium">Choisir un template</span>
                    <span className="text-xs text-gray-500">42 templates disponibles</span>
                  </div>
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Template Selector Modal */}
          <TemplateSelector
            open={templateSelectorOpen}
            onOpenChange={setTemplateSelectorOpen}
            onSelectTemplate={handleTemplateFromSelector}
          />

          {/* Message Editor */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Message RCS
              </CardTitle>
              {appliedTemplate && (
                <CardDescription className="text-indigo-600">
                  Personnalisez le template ou modifiez le message
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <RCSMessageEditor
                key={editorKey}
                initialContent={messageContent?.content}
                onPreviewChange={handlePreviewChange}
              />
            </CardContent>
          </Card>

          {/* Test & SMS Preview Section - AFTER editor, BEFORE send button */}
          <Card className="border-indigo-200 bg-indigo-50/30">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="w-5 h-5 text-indigo-600" />
                Test & Prévisualisation
              </CardTitle>
              <CardDescription>
                Testez votre message RCS et prévisualisez le SMS fallback avant l'envoi
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!messageContent ? (
                <div className="text-center py-4 text-slate-500">
                  <Eye className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Créez d'abord votre message pour activer la prévisualisation</p>
                </div>
              ) : (
                <>
                  {/* Test Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="test-phone">Numéro de test RCS</Label>
                    <div className="flex gap-2">
                      <Input
                        id="test-phone"
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                        placeholder="+33612345678"
                        className="flex-1"
                        data-testid="test-phone-input"
                      />
                      <Button
                        variant="outline"
                        disabled={!testPhone || !messageContent}
                        onClick={() => {
                          // TODO: Implement test send
                          toast.info('Envoi de test en cours...');
                        }}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Tester RCS
                      </Button>
                    </div>
                  </div>

                  {/* SMS Fallback Options */}
                  <div className="p-4 bg-white rounded-lg border border-indigo-200 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MessageCircle className="w-5 h-5 text-indigo-500" />
                        <span className="font-medium">SMS Fallback</span>
                      </div>
                      <Switch 
                        checked={smsFallbackEnabled}
                        onCheckedChange={setSmsFallbackEnabled}
                        data-testid="sms-fallback-toggle"
                      />
                    </div>

                    {smsFallbackEnabled && (
                      <>
                        {/* Marketing vs Transactional */}
                        <div className="flex items-center justify-between py-2 border-t border-indigo-100">
                          <div>
                            <p className="text-sm font-medium">Type de message</p>
                            <p className="text-xs text-slate-500">
                              {isMarketing 
                                ? 'Marketing: lien STOP ajouté (RGPD)'
                                : 'Transactionnel: pas de lien STOP'
                              }
                            </p>
                          </div>
                          <Select value={isMarketing ? 'marketing' : 'transactional'} onValueChange={(v) => setIsMarketing(v === 'marketing')}>
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="marketing">Marketing</SelectItem>
                              <SelectItem value="transactional">Transactionnel</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Preview SMS Button */}
                        <Button
                          variant="outline"
                          className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                          onClick={() => setSmsPreviewOpen(true)}
                          disabled={!messageContent}
                          data-testid="preview-sms-btn"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Prévisualiser le SMS Fallback
                        </Button>
                      </>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Send Button */}
          <Button
            onClick={handleSendMessages}
            disabled={sending || !messageContent || getPhonesList().length === 0}
            className="w-full bg-indigo-600 hover:bg-indigo-700 h-12 text-lg"
            data-testid="send-messages-btn"
          >
            {sending ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Envoyer les messages
              </>
            )}
          </Button>

          {/* Send Results */}
          {sendResults && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <span className="font-semibold text-green-800">Envoi terminé</span>
                </div>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-xl font-bold">{sendResults.total}</div>
                    <div className="text-xs text-gray-600">Total</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-green-600">{sendResults.rcs_sent}</div>
                    <div className="text-xs text-gray-600">RCS envoyés</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-indigo-600">{sendResults.sms_fallback}</div>
                    <div className="text-xs text-gray-600">SMS fallback</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-red-600">{sendResults.failed}</div>
                    <div className="text-xs text-gray-600">Échoués</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          </div>
        </div>

        {/* Right Column - Preview */}
        <div className="lg:sticky lg:top-24 lg:h-fit">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Smartphone className="w-5 h-5" />
                Aperçu Mobile
              </CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center py-6">
              <RCSMobilePreview 
                content={messageContent}
                senderName={user?.role === 'superadmin' 
                  ? clients.find(c => c.id === selectedClient)?.name || 'Votre Marque'
                  : 'Votre Marque'
                }
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Contact File Selector Modal */}
      <ContactFileSelector
        open={contactFileSelectorOpen}
        onOpenChange={setContactFileSelectorOpen}
        onSelectFile={handleContactFileSelect}
        templateVariables={getTemplateVariables()}
      />

      {/* SMS Preview Modal */}
      <SMSPreviewModal
        open={smsPreviewOpen}
        onOpenChange={setSmsPreviewOpen}
        messageContent={messageContent}
        senderName={user?.role === 'superadmin' 
          ? clients.find(c => c.id === selectedClient)?.name || 'Votre Marque'
          : user?.client_name || 'Votre Marque'
        }
        isMarketing={isMarketing}
      />
    </>
  );
  
  // Si embedded, retourner sans Layout
  if (embedded) {
    return content;
  }
  
  return <Layout>{content}</Layout>;
}
